'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * V2.11 (2026-05-27 phase 3.1.a) — voice WebSocket hook.
 *
 * Scaffold only. Connect → exchange JSON messages → disconnect. 3.1.b
 * will extend this with PCM audio streaming hooks (sendAudioChunk),
 * and 3.2.a will plumb audio playback for inbound TTS frames.
 *
 * The wire endpoint is `wss://<host>/api/voice/ws`, which the Next.js
 * edge proxies through to the orchestrator Worker, which proxies to
 * Hermes via the singleton DO.
 */

export type VoiceWsMessage = { type: string; [k: string]: unknown };
export type VoiceWsStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export function useVoiceWs(
  options: {
    /** Auto-connect on mount (default true). */
    autoConnect?: boolean;
    /** Server-sent JSON messages land here. */
    onMessage?: (m: VoiceWsMessage) => void;
    /** Server-sent binary frames (audio chunks in 3.2.a). */
    onBinary?: (b: ArrayBuffer) => void;
  } = {},
) {
  const [status, setStatus] = useState<VoiceWsStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const onMsgRef = useRef(options.onMessage);
  const onBinRef = useRef(options.onBinary);
  // V3.4 (2026-06-03 audit H8) — track last activity from the server.
  // CF Workers proxies kill idle WebSockets at ~100s. Hermes now sends
  // {type:"ping",ts:...} every 25s and we reply with {type:"pong"}. If
  // 60s pass without ANY server frame (text or binary), the heartbeat
  // watchdog force-closes so the chat-loop's asr_error reconnect kicks
  // in. Set to 0 (=epoch) until first message arrives.
  const lastServerMsgAtRef = useRef<number>(0);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // 2026-06-26 (Leon console R41) — coalesce concurrent connect() calls.
  // Before: enterVoice() + startTurn() + resetVoice() + visibility
  // reconnect each independently passed the `wsRef.current is null`
  // guard, minted a token, opened a fresh WS. Multiple WS sessions
  // existed in parallel; only one ever got Hermes' asr_started ack;
  // PCM frames raced into the wrong session. asr_ready=false +
  // endTurn watchdog firing on every turn was the symptom.
  // Now: any concurrent connect() returns the same in-flight Promise.
  const inflightConnectRef = useRef<Promise<void> | null>(null);

  // Latest callbacks without re-instantiating the WS on every render.
  useEffect(() => {
    onMsgRef.current = options.onMessage;
  }, [options.onMessage]);
  useEffect(() => {
    onBinRef.current = options.onBinary;
  }, [options.onBinary]);

  const connect = useCallback(async () => {
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return;
    // Coalesce concurrent connect() calls (see comment above).
    if (inflightConnectRef.current) {
      console.warn('[VOICE] connect: coalescing into in-flight connect call');
      return inflightConnectRef.current;
    }
    let resolveInflight!: () => void;
    let rejectInflight!: (e: unknown) => void;
    inflightConnectRef.current = new Promise<void>((res, rej) => {
      resolveInflight = res;
      rejectInflight = rej;
    });
    // Clean up the in-flight latch whenever this attempt resolves OR
    // rejects, so a later retry isn't permanently stuck waiting on a
    // dead promise.
    inflightConnectRef.current.finally(() => {
      inflightConnectRef.current = null;
    });
    try {
      await connectInner();
      resolveInflight();
    } catch (e) {
      rejectInflight(e);
      throw e;
    }
  }, []);

  // The real connect body, factored out so the coalescing wrapper above
  // can resolve/reject the in-flight promise.
  const connectInner = useCallback(async () => {
    setStatus('connecting');
    setError(null);

    // 2026-06-15 P4-3 — Amy 报「试聊语音不 work」无 trace. [VOICE] 前缀
    // 串起 connect 全流程, 截图就能定位失败阶段. console.warn (不是 info)
    // 是为了 biome 不把它当 noop 自动删.
    console.warn('[VOICE] connect: start', {
      origin: typeof location !== 'undefined' ? location.origin : 'n/a',
      apiUrl: process.env.NEXT_PUBLIC_API_URL ?? '(unset)',
    });

    let token: string;
    try {
      console.warn('[VOICE] connect: minting session-token');
      const r = await fetch('/api/voice/session-token', {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        setStatus('error');
        const msg =
          r.status === 401
            ? '未登录或会话已失效，请先登录此域名'
            : `token mint failed (${r.status})`;
        console.error('[VOICE] connect: token mint failed', { status: r.status, msg });
        setError(msg);
        throw new Error(msg);
      }
      const body = (await r.json()) as { token?: string };
      if (!body.token) {
        console.error('[VOICE] connect: no token in response body', body);
        setStatus('error');
        setError('no token returned');
        throw new Error('no token returned');
      }
      token = body.token;
      console.warn('[VOICE] connect: token minted ok');
    } catch (e) {
      setStatus('error');
      const msg = e instanceof Error ? e.message : 'token mint network error';
      console.error('[VOICE] connect: token-mint exception', e);
      setError(msg);
      throw new Error(msg);
    }

    const apiBase = (process.env.NEXT_PUBLIC_API_URL ?? '').replace(/^http/, 'ws');
    if (!apiBase) {
      console.error('[VOICE] connect: NEXT_PUBLIC_API_URL not baked at build time');
      setStatus('error');
      setError('语音服务地址未配置 (NEXT_PUBLIC_API_URL missing) — 重新部署 staging 应能修复');
      return;
    }
    const wsUrl = `${apiBase}/voice/ws?token=${encodeURIComponent(token)}`;
    console.warn('[VOICE] connect: opening ws', {
      host: apiBase,
    });
    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => {
      console.warn('[VOICE] ws: open');
      setStatus('open');
      lastServerMsgAtRef.current = Date.now();
      // V3.4 (2026-06-03 audit H8) — start heartbeat watchdog. We DO
      // NOT actively ping from the client because hermes already pings
      // every 25s and we reply pong; the watchdog just verifies we hear
      // *something* from the server within 60s windows. Stale window =
      // force close → chat-loop sees onclose / next send fails → its
      // asr_error reconnect path takes over.
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = setInterval(() => {
        const w = wsRef.current;
        if (!w || w.readyState !== WebSocket.OPEN) return;
        const since = Date.now() - lastServerMsgAtRef.current;
        if (since > 60_000) {
          // 60s of silence — assume CF Workers killed the relay. Force
          // close so the chat-loop's reconnect path kicks in.
          try {
            w.close(4001, 'heartbeat_timeout');
          } catch {
            /* ignore */
          }
        }
      }, 15_000);
    };
    ws.onclose = (e) => {
      console.warn('[VOICE] ws: close', {
        code: e.code,
        reason: e.reason,
        wasClean: e.wasClean,
      });
      setStatus('closed');
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };
    ws.onerror = (e) => {
      console.error('[VOICE] ws: error', e);
      setStatus('error');
      setError(e instanceof ErrorEvent ? e.message : 'websocket error');
    };
    ws.onmessage = (e) => {
      if (typeof e.data === 'string') {
        // V3.4 (2026-06-03 audit H8) — any server text frame counts as
        // liveness. Track lastServerMsgAt so the heartbeat watchdog can
        // detect CF Workers' ~100s idle close and force a reconnect.
        lastServerMsgAtRef.current = Date.now();
        try {
          const m = JSON.parse(e.data) as VoiceWsMessage;
          // V3.4 (2026-06-03 audit H8) — server-initiated 25s heartbeat.
          // Hermes sends {type:"ping",ts:...} every 25s while the WS is
          // open; we reply {type:"pong"} so the orchestrator + any CF
          // intermediate proxy sees activity. The text-frame-as-liveness
          // bump above already updates lastServerMsgAt; the explicit pong
          // reply also keeps the upstream Doubao keepalive logic happy.
          if (m.type === 'ping') {
            try {
              ws.send(JSON.stringify({ type: 'pong', ts: Date.now() }));
            } catch {
              /* socket may have just closed; reconnect path will handle */
            }
            return;
          }
          if (m.type === 'pong') {
            // Server replying to our pong-echo, or its own pong. Just
            // count as liveness (already bumped above).
            return;
          }
          // V3.4 (2026-06-03 audit V1) — FIFO ack queue. Each
          // asr_started / asr_ended frame releases exactly one waiter in
          // arm-order, so a slow ack can't be silently lost when a new
          // waiter installs itself.
          if (m.type === 'asr_started') {
            resolveAck('started');
          } else if (m.type === 'asr_ended') {
            resolveAck('ended');
          }
          onMsgRef.current?.(m);
        } catch {
          // bad frame — ignore silently for the scaffold
        }
      } else if (e.data instanceof ArrayBuffer) {
        lastServerMsgAtRef.current = Date.now();
        onBinRef.current?.(e.data);
      }
    };
    wsRef.current = ws;
  }, []);

  const disconnect = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    wsRef.current?.close(1000, 'client_disconnect');
    wsRef.current = null;
  }, []);

  const sendJson = useCallback((msg: VoiceWsMessage): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(msg));
    return true;
  }, []);

  const sendBinary = useCallback((b: ArrayBuffer): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(b);
    return true;
  }, []);

  // V2.11 (2026-05-27 phase 3.1.b) — mic capture → 16 kHz Int16 PCM
  // → binary WS frames upstream → Doubao streaming ASR. Recording state
  // lives in refs so changing it doesn't restart the hook.
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  // 2026-06-23 — kept for backward compat. New code should use
  // setMicEnabled() instead — hardware-level pause via track.enabled,
  // which doesn't fool Doubao's silence detection.
  const sendingPausedRef = useRef(false);
  const [recording, setRecording] = useState(false);
  // 2026-06-23 v3 — mic enabled state. False = audio track muted at
  // the hardware level (track.enabled=false). MediaStream + AudioContext
  // + AudioWorklet stay alive; Doubao still receives PCM but it's
  // genuine silence samples that get filtered at the chat-loop layer.
  const [micEnabled, setMicEnabledState] = useState(true);
  // 2026-06-23 v3 — structured logger. Every state transition emits a
  // single-line JSON event tagged `[voice-ws]` so we can grep dev tools
  // when something stuck. Cheap; only runs when console is open.
  function vlog(event: string, extra: Record<string, unknown> = {}) {
    if (typeof console === 'undefined') return;
    const ws = wsRef.current;
    console.warn(
      `[voice-ws] ${event}`,
      JSON.stringify({
        ws_state: ws
          ? ws.readyState === WebSocket.OPEN
            ? 'open'
            : ws.readyState === WebSocket.CONNECTING
              ? 'connecting'
              : 'closed/closing'
          : 'null',
        asr_ready: asrReadyRef.current,
        mic_track_enabled: micStreamRef.current?.getAudioTracks()[0]?.enabled ?? null,
        buf_chunks: pcmBufferRef.current.length,
        ...extra,
      }),
    );
  }

  // V3.4 (2026-06-03) — startup-gap PCM buffer. Between getUserMedia
  // returning and hermes's asr_started ack arriving, the worklet is
  // already producing PCM frames but hermes hasn't opened its Doubao
  // session yet (Doubao WS handshake from CF Singapore ~500ms–3s under
  // load). Previously we ws.send(pcm) unconditionally; hermes dropped
  // those frames because `asr is None`. The user thought the mic was
  // hot, spoke 「我想问的是…」, and the first 1-3s got eaten.
  //
  // Fix: buffer PCM in a ring (capped at ~5s of 16kHz mono Int16
  // = 160KB) while `asrReadyRef.current === false`. On asr_started,
  // drain the buffer into the WS in one batch, then switch to direct
  // passthrough. On stopRecording, clear the buffer.
  const asrReadyRef = useRef(false);
  const pcmBufferRef = useRef<ArrayBuffer[]>([]);
  // 5 seconds @ 16kHz mono Int16 = 160_000 bytes. Above this we drop
  // the oldest chunk so the buffer never grows unbounded if the ack
  // never arrives (the 6s ack timeout will still throw, but we don't
  // want to OOM the tab in pathological cases).
  const PCM_BUFFER_MAX_BYTES = 160_000;
  function pcmBufferTotalBytes(): number {
    let total = 0;
    for (const b of pcmBufferRef.current) total += b.byteLength;
    return total;
  }
  function pcmBufferPush(buf: ArrayBuffer) {
    pcmBufferRef.current.push(buf);
    while (pcmBufferTotalBytes() > PCM_BUFFER_MAX_BYTES && pcmBufferRef.current.length > 1) {
      pcmBufferRef.current.shift();
    }
  }
  function pcmBufferFlush(ws: WebSocket) {
    if (ws.readyState !== WebSocket.OPEN) {
      pcmBufferRef.current = [];
      return;
    }
    for (const buf of pcmBufferRef.current) {
      try {
        ws.send(buf);
      } catch {
        // WS closed mid-flush; bail
        break;
      }
    }
    pcmBufferRef.current = [];
  }

  // V2.11 (2026-05-28) — persistent AudioContext. Chrome on macOS has a
  // known bug where rapidly create/closing AudioContext (especially while
  // Continuity Camera/Mic is connected) puts the context into a permanent
  // error state — the AudioContext logs
  //   "The AudioContext encountered an error from the audio device or
  //    the WebAudio renderer."
  // and no PCM frames ever reach the worklet. So we now reuse one
  // AudioContext across all turns: suspend on stop, resume on start.
  // The mic MediaStream itself is still fresh every turn (browser
  // permission requirement).
  const audioWorkletReadyRef = useRef(false);
  const audioCtxErrorRef = useRef<string | null>(null);
  // V3.4 (2026-06-03 audit V1) — ack resolvers are now FIFO queues, not
  // single slots. The previous design did `ref.current = null` BEFORE
  // installing the new resolver in waitForAck. If a prior asr_started
  // arrived in that window, the onmessage handler saw a null ref and
  // silently dropped the ack — the freshly-installed waiter then timed
  // out at 6s for an ack that already arrived. Symptom: rapid retry
  // inside the warmup window hangs forever.
  //
  // FIFO queue: waitForAck pushes onto the tail; onmessage shifts the
  // head. New waiters installed during in-flight resolution simply queue
  // up; each ack release pops exactly one waiter in the order they were
  // armed.
  type AckEntry = {
    resolve: (v: 'ok' | 'timeout') => void;
    timeoutId: ReturnType<typeof setTimeout>;
  };
  const asrStartedAckQueueRef = useRef<AckEntry[]>([]);
  const asrEndedAckQueueRef = useRef<AckEntry[]>([]);

  function waitForAck(slot: 'started' | 'ended', timeoutMs: number): Promise<'ok' | 'timeout'> {
    const queue = slot === 'started' ? asrStartedAckQueueRef.current : asrEndedAckQueueRef.current;
    return new Promise((resolve) => {
      const entry: AckEntry = {
        resolve,
        timeoutId: setTimeout(() => {
          // Remove ourselves from the queue if still present.
          const idx = queue.indexOf(entry);
          if (idx >= 0) queue.splice(idx, 1);
          resolve('timeout');
        }, timeoutMs),
      };
      queue.push(entry);
    });
  }

  function resolveAck(slot: 'started' | 'ended'): void {
    const queue = slot === 'started' ? asrStartedAckQueueRef.current : asrEndedAckQueueRef.current;
    const head = queue.shift();
    if (!head) return;
    clearTimeout(head.timeoutId);
    head.resolve('ok');
  }

  async function ensureAudioCtx(): Promise<AudioContext> {
    let ctx = audioCtxRef.current;
    if (ctx && ctx.state !== 'closed') {
      if (ctx.state === 'suspended') await ctx.resume();
      return ctx;
    }
    // Fresh context. Don't pin sampleRate — the worklet decimates.
    ctx = new AudioContext();
    audioCtxRef.current = ctx;
    ctx.onstatechange = () => {
      const s = ctx?.state;
      if (s === 'closed') {
        audioCtxErrorRef.current = 'audio_context_closed_unexpectedly';
      }
    };
    if (!audioWorkletReadyRef.current) {
      await ctx.audioWorklet.addModule('/audio-worklet/pcm16-recorder.js');
      audioWorkletReadyRef.current = true;
    }
    return ctx;
  }

  // V3.4 (2026-06-03 audit V2) — startPromiseRef replaces the boolean
  // startingRef. The old guard `if (startingRef.current) return` silently
  // dropped concurrent calls, so the asr_error reconnect path's
  // startRecording could no-op while the caller proceeded to set phase
  // to 'listening' against a dead mic. The new design: if a start is
  // in flight, new callers AWAIT it and inherit its resolution. Same
  // pattern stopPromiseRef already uses.
  const startPromiseRef = useRef<Promise<void> | null>(null);
  // V3.4 (2026-06-03) — track in-flight stopRecording so that a rapid
  // pause → resume (user taps 「已暂停 · 点击继续」 within 1-2s of the
  // pause) doesn't race hermes's Doubao session shutdown. Without this,
  // startRecording's audio_start can arrive on the WS while hermes is
  // still inside `await asr_task` from the prior stop; hermes either
  // queues it (delays asr_started past our 6s ack timeout) or drops it
  // (silent fail — UI shows 'listening' but no PCM gets routed).
  // Symptom: 「点击继续」 looks broken — phase flips but nothing happens.
  const stopPromiseRef = useRef<Promise<void> | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: ensureAudioCtx is a stable inner helper
  const startRecording = useCallback(
    async (mode: 'realtime' | 'ptt' = 'realtime'): Promise<void> => {
      if (startPromiseRef.current) {
        // Coalesce concurrent callers onto the in-flight start. They
        // inherit its success/failure — no silent drop.
        return startPromiseRef.current;
      }
      const run = async (): Promise<void> => {
        // Drain any in-flight stopRecording before starting fresh. This
        // serialises audio_end ← asr_ended ack ← audio_start at the WS
        // layer, which is the contract hermes assumes.
        if (stopPromiseRef.current) {
          await stopPromiseRef.current.catch(() => {});
        }
        // V2.11 (2026-05-28d) — wait up to ~15s for the WS handshake to
        // complete. Was 3s; that's not enough when the CF Worker is cold
        // and the DO container is spinning up. The wait is split into
        // a wsRef-population window (rarely > 1s) + an OPEN window.
        const POPULATE_WINDOW_MS = 2000;
        const OPEN_WINDOW_MS = 15000;
        const TICK_MS = 50;

        let ws = wsRef.current;
        for (let i = 0; i < POPULATE_WINDOW_MS / TICK_MS && !ws; i++) {
          await new Promise((r) => setTimeout(r, TICK_MS));
          ws = wsRef.current;
        }
        if (!ws) throw new Error('websocket 未建立连接（前置握手失败，检查网络或登录状态）');
        const start = Date.now();
        while (Date.now() - start < OPEN_WINDOW_MS) {
          if (ws.readyState === WebSocket.OPEN) break;
          if (ws.readyState === WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED) {
            // 2026-06-24 v14 — friendly user-facing text. The raw debug
            // string ("建立连接后立即断开，检查后端 voice/ws 路由") was
            // leaking into the UI banner (Codex R25-F2). Detail goes to
            // console only.
            console.error('[VOICE] ws closed during connect window', {
              readyState: ws.readyState,
              elapsed_ms: Date.now() - start,
            });
            throw new Error('语音连接中断，请稍候再试');
          }
          await new Promise((r) => setTimeout(r, TICK_MS));
        }
        if (ws.readyState !== WebSocket.OPEN) {
          console.error('[VOICE] ws not OPEN after handshake window', {
            readyState: ws.readyState,
            elapsed_ms: Date.now() - start,
          });
          throw new Error('语音连接超时，请稍候再试');
        }

        // Tear down any leftover worklet from a previous turn (but KEEP the
        // AudioContext alive — see comment on ensureAudioCtx).
        try {
          workletNodeRef.current?.disconnect();
        } catch {}
        workletNodeRef.current = null;
        const oldStream = micStreamRef.current;
        micStreamRef.current = null;
        if (oldStream) for (const t of oldStream.getTracks()) t.stop();

        // 2026-07-02 (Leon 反馈) — inline try/catch so we can dispatch the
        // global mic-permission-denied event before the error propagates
        // up to the outer failure path. Otherwise the WS voice loop just
        // shows a generic error and Android users have no clue what to do.
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              channelCount: 1,
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          });
        } catch (mediaErr) {
          try {
            const { reportMicError } = await import('./mic-permission');
            reportMicError(mediaErr);
          } catch {
            /* module missing — silent */
          }
          throw mediaErr;
        }
        micStreamRef.current = stream;
        // 2026-06-23 v3 — always start the freshly-acquired stream in the
        // enabled state. Callers can flip via setMicEnabled() after.
        // (We do NOT honour a pre-existing micEnabled=false state, because
        // a fresh openMic implies the caller wants the mic live.)
        for (const t of stream.getAudioTracks()) {
          t.enabled = true;
        }
        setMicEnabledState(true);
        vlog('start_recording_stream_acquired');

        const ctx = await ensureAudioCtx();
        if (ctx.state === 'closed' || audioCtxErrorRef.current) {
          throw new Error(audioCtxErrorRef.current ?? '麦克风初始化失败');
        }
        // V3.4 (2026-06-03) — reset buffer + ready flag for this session.
        asrReadyRef.current = false;
        pcmBufferRef.current = [];
        const node = new AudioWorkletNode(ctx, 'pcm16-recorder');
        node.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
          // V3.4 (2026-06-03 audit V3) — always read wsRef.current here
          // instead of the closure-captured `ws`. If disconnect() swapped
          // wsRef during the warmup window, the captured `ws` points at a
          // dead socket; meanwhile asr_started ack arrives on the new
          // socket and resolves our waiter — leaving asrReadyRef=true but
          // the next ws.send no-oping silently. Reading the live ref
          // means we either send on the right socket or the readyState
          // guard drops the frame.
          const liveWs = wsRef.current;
          if (!liveWs || liveWs.readyState !== WebSocket.OPEN) return;
          // 2026-06-23 — sendingPausedRef: when set, drop frames entirely
          // (no zero-fill — Doubao's end_window_size: 800 counts zeros as
          // silence and degrades sessions over time). Kept the ref for
          // future use; currently only stop/start path uses it implicitly
          // via reconnect on next start.
          if (sendingPausedRef.current) return;
          if (asrReadyRef.current) {
            liveWs.send(e.data);
          } else {
            // Doubao session not yet open on hermes — stash so we can
            // flush on asr_started ack. Without this, the first 1-3s of
            // speech gets dropped server-side.
            pcmBufferPush(e.data);
          }
        };
        workletNodeRef.current = node;
        const source = ctx.createMediaStreamSource(stream);
        source.connect(node);

        // Sanity check: if the context is suspended right now (autoplay
        // policy), explicitly resume so the worklet actually produces
        // frames. Without this, we get a 0-PCM stream and Doubao times out
        // at 8s.
        if (ctx.state === 'suspended') await ctx.resume().catch(() => {});

        // V2.11 (2026-05-28f) — arm the asr_started ack BEFORE sending
        // audio_start so we don't miss the response. 2026-06-24 v10 —
        // bumped 6s→12s after Codex round 23 caught second-PTT-after-turn
        // failing with `asr_started ack timeout`. Worst-case stack:
        // CF Container cold-start ~1-2s + Doubao handshake from Singapore
        // ~3-4s + hermes asr_session_lock contention with still-tearing-
        // down prior session ~5s. 12s covers all three; the user is
        // already staring at the warmup bar so the wait cost is small
        // and explainable.
        const ack = waitForAck('started', 12000);
        // 2026-06-24 v8 — pass mode to hermes so Doubao's endpoint detection
        // matches: realtime uses 800ms (auto-commit on natural pauses), PTT
        // uses 60000ms (user controls end via 结束发送 → audio_end).
        ws.send(JSON.stringify({ type: 'audio_start', sample_rate: 16000, mode }));
        const ackResult = await ack;
        if (ackResult === 'timeout') {
          throw new Error('asr_started ack timeout: 后端 Doubao 会话未及时建立');
        }
        // V3.4 (2026-06-03 audit V3) — revalidate the socket after the
        // await. If disconnect()+connect() swapped wsRef during the ack
        // wait, the asr_started we just consumed actually belongs to the
        // NEW socket (queue is module-level on the hook). Sending pending
        // PCM down the captured-but-now-dead `ws` is silent dropping;
        // flushing into the new socket would mix old + new PCM. Bail and
        // let the caller (asr_error reconnect, or new explicit start)
        // restart cleanly.
        if (ws !== wsRef.current) {
          throw new Error('websocket_swapped_during_start');
        }
        // Drain the warmup buffer in one go BEFORE flipping the ready
        // flag, so any new worklet frames arriving concurrently take
        // the direct ws.send path (preserving order at the WS layer).
        pcmBufferFlush(ws);
        asrReadyRef.current = true;
        setRecording(true);
      };
      const p = run();
      startPromiseRef.current = p;
      try {
        await p;
      } finally {
        if (startPromiseRef.current === p) startPromiseRef.current = null;
      }
    },
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: waitForAck is a stable inner helper closing over refs only
  const stopRecording = useCallback(async (): Promise<void> => {
    // V3.4 (2026-06-03) — publish in-flight stop promise so the next
    // startRecording can await it (see stopPromiseRef contract above).
    const run = async () => {
      const ws = wsRef.current;
      let ackPromise: Promise<'ok' | 'timeout'> | null = null;
      if (ws && ws.readyState === WebSocket.OPEN) {
        // V2.11 (2026-05-28f) — arm ack BEFORE sending so we don't miss
        // hermes's asr_ended response.
        ackPromise = waitForAck('ended', 8000);
        ws.send(JSON.stringify({ type: 'audio_end' }));
      }
      // V3.4 (2026-06-03) — clear warmup buffer + flag for the next
      // startRecording cycle. Don't flush — Doubao session is closing.
      asrReadyRef.current = false;
      pcmBufferRef.current = [];
      try {
        workletNodeRef.current?.disconnect();
      } catch {}
      workletNodeRef.current = null;
      // KEEP audioCtxRef.current alive — just suspend it. This avoids
      // Chrome's "encountered an error" bug on rapid open/close cycles.
      if (audioCtxRef.current && audioCtxRef.current.state === 'running') {
        await audioCtxRef.current.suspend().catch(() => {});
      }
      for (const t of micStreamRef.current?.getTracks() ?? []) t.stop();
      micStreamRef.current = null;
      setRecording(false);
      // Block on asr_ended ack so the next startRecording's audio_start
      // arrives at hermes AFTER the prior session is fully torn down.
      // Without this, audio_start sits in the WS buffer while hermes is
      // still doing `await asr_task` (up to 5s) and any new PCM frames
      // get dropped because asr is None.
      if (ackPromise) await ackPromise;
    };
    const p = run();
    stopPromiseRef.current = p;
    try {
      await p;
    } finally {
      if (stopPromiseRef.current === p) stopPromiseRef.current = null;
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once intentional — connect/disconnect are stable useCallbacks with [] deps
  useEffect(() => {
    if (options.autoConnect ?? true) connect();
    return () => {
      // Final cleanup — on actual hook unmount we DO close the
      // AudioContext (it's the persistent one held across turns).
      try {
        workletNodeRef.current?.disconnect();
      } catch {}
      for (const t of micStreamRef.current?.getTracks() ?? []) t.stop();
      audioCtxRef.current?.close().catch(() => {});
      audioWorkletReadyRef.current = false;
      disconnect();
    };
  }, []);

  // 2026-06-23 v2 — soft sending pause (drop frames). Kept for back-
  // compat but supplanted by setMicEnabled. Don't use in new code.
  const pauseSending = useCallback(() => {
    sendingPausedRef.current = true;
  }, []);
  const resumeSending = useCallback(() => {
    sendingPausedRef.current = false;
  }, []);

  // 2026-06-23 v3 — hardware-level mic pause. Toggles
  // MediaStreamAudioTrack.enabled, which produces real-silence samples
  // (zero amplitude) without tearing down getUserMedia or AudioContext.
  // The AudioWorklet keeps running, PCM keeps flowing to Doubao at the
  // same rate; the difference is samples = 0 instead of mic audio.
  // Doubao's end_window_size: 800 will trigger empty definite=true
  // utterances, which the chat-loop filters out by checking text.trim()
  // length before fireFinal.
  //
  // This is the Doubao app's pattern: pause means muted-mic, not
  // session-teardown.
  // biome-ignore lint/correctness/useExhaustiveDependencies: vlog is a stable inner closure
  const setMicEnabled = useCallback((enabled: boolean) => {
    const stream = micStreamRef.current;
    if (!stream) {
      vlog('set_mic_enabled_no_stream', { requested: enabled });
      // Track the desired state even without a stream so the next
      // openMic can apply it. (Rare — only happens if caller toggles
      // before startRecording resolves.)
      setMicEnabledState(enabled);
      return;
    }
    for (const t of stream.getAudioTracks()) {
      t.enabled = enabled;
    }
    setMicEnabledState(enabled);
    vlog('mic_enabled_changed', { enabled });
  }, []);

  return {
    status,
    error,
    connect,
    disconnect,
    sendJson,
    sendBinary,
    startRecording,
    stopRecording,
    pauseSending,
    resumeSending,
    setMicEnabled,
    recording,
    micEnabled,
  };
}
